import { useEffect, useState } from 'react';
import { FloppyDisk, Eye, UploadSimple, FilePdf, Plus, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { DiseaseGroupFilter } from '../DiseaseGroupFilter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProtocolPanel({ studyId, lang }) {
  const [protocolDoc, setProtocolDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [protocolData, setProtocolData] = useState({});
  const [protocolFields, setProtocolFields] = useState([]);
  const [savingData, setSavingData] = useState(false);

  const lb = lang === 'es'
    ? { doc_title: 'Documento del Protocolo', upload: 'Subir protocolo (PDF)', change: 'Cambiar documento', view: 'Ver protocolo', no_doc: 'No se ha subido ningun documento de protocolo', fields_title: 'Datos del Protocolo', save_data: 'Guardar datos', saved: 'Datos del protocolo guardados', select_placeholder: 'Seleccionar...', remove: 'Eliminar', confirm_remove_title: 'Eliminar protocolo', confirm_remove_desc: '¿Eliminar permanentemente este PDF del protocolo? Se borrará tanto del almacenamiento como del estudio.', removed: 'Protocolo eliminado' }
    : { doc_title: 'Protocol Document', upload: 'Upload protocol (PDF)', change: 'Change document', view: 'View protocol', no_doc: 'No protocol document uploaded', fields_title: 'Protocol Data', save_data: 'Save data', saved: 'Protocol data saved', select_placeholder: 'Select...', remove: 'Remove', confirm_remove_title: 'Remove protocol', confirm_remove_desc: 'Permanently remove this protocol PDF? It will be deleted from storage and the study.', removed: 'Protocol removed' };

  useEffect(() => {
    if (!studyId) return;
    Promise.all([
      axios.get(`${API_URL}/api/studies/${studyId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/protocol-fields`, { withCredentials: true }),
    ]).then(([sr, fr]) => {
      const s = sr.data;
      // URS-F283: siempre reflejar el estado real del servidor (incluido null
      // tras borrado), no solo cuando hay doc. Antes el estado quedaba stale
      // si la respuesta venia con protocol_doc=null.
      setProtocolDoc(s.protocol_doc || null);
      if (s.protocol_data) setProtocolData(s.protocol_data);
      setProtocolFields(fr.data);
    }).catch(() => {});
  }, [studyId]);

  const handleUpload = async (file) => {
    if (!studyId) { toast.error(lang === 'es' ? 'Guarda el estudio primero' : 'Save the study first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const upRes = await axios.post(`${API_URL}/api/files/upload`, fd, { withCredentials: true });
      // URS-F281: el endpoint /api/files/upload devuelve el campo `id`, no `file_id`.
      // Antes guardabamos `file_id: undefined`, por eso parecia que se subia pero no
      // se podia descargar despues (404 contra /api/files/download/undefined).
      const doc = { file_id: upRes.data.id, file_name: file.name, uploaded_at: new Date().toISOString() };
      await axios.put(`${API_URL}/api/studies/${studyId}`, { protocol_doc: doc }, { withCredentials: true });
      setProtocolDoc(doc);
      toast.success(lang === 'es' ? 'Protocolo subido' : 'Protocol uploaded');
    } catch (err) { toast.error(errText(err)); }
    setUploading(false);
  };

  const openDoc = () => {
    if (protocolDoc?.file_id) window.open(`${API_URL}/api/files/download/${protocolDoc.file_id}`, '_blank');
  };

  const handleRemove = async () => {
    if (!studyId || !protocolDoc?.file_id) return;
    setRemoving(true);
    try {
      await axios.delete(`${API_URL}/api/files/${protocolDoc.file_id}`, { withCredentials: true });
      await axios.put(`${API_URL}/api/studies/${studyId}`, { protocol_doc: null }, { withCredentials: true });
      setProtocolDoc(null);
      setConfirmRemove(false);
      toast.success(lb.removed);
    } catch (err) {
      toast.error(errText(err));
    }
    setRemoving(false);
  };

  const setFieldValue = (fieldId, value) => {
    setProtocolData(p => ({ ...p, [fieldId]: value }));
  };

  const toggleMultiselect = (fieldId, option) => {
    setProtocolData(p => {
      const current = Array.isArray(p[fieldId]) ? p[fieldId] : [];
      const next = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
      return { ...p, [fieldId]: next };
    });
  };

  const saveProtocolData = async () => {
    if (!studyId) return;
    setSavingData(true);
    try {
      // Strip transient input keys (only _new_custom_*) before saving.
      // We keep _custom_options_* which is the persistent per-study source.
      const cleaned = Object.fromEntries(
        Object.entries(protocolData).filter(([k]) => !k.startsWith('_new_custom_'))
      );
      await axios.put(`${API_URL}/api/studies/${studyId}`, { protocol_data: cleaned }, { withCredentials: true });
      const visitField = protocolFields.find(f => f.label.toLowerCase().includes('visitas') && f.type === 'multiselect');
      if (visitField) {
        const selectedCodes = cleaned[visitField.field_id];
        if (Array.isArray(selectedCodes) && selectedCodes.length > 0) {
          const syncRes = await axios.post(`${API_URL}/api/studies/${studyId}/sync-protocol-visits`, { visit_codes: selectedCodes }, { withCredentials: true });
          if (syncRes.data.created > 0) {
            toast.success(`${syncRes.data.created} ${lang === 'es' ? 'visitas creadas en el protocolo' : 'visits created from protocol'}`);
          }
        }
      }
      const inclusionField = protocolFields.find(f => f.label.toLowerCase().includes('inclusión') || f.label.toLowerCase().includes('inclusion'));
      const exclusionField = protocolFields.find(f => f.label.toLowerCase().includes('exclusión') || f.label.toLowerCase().includes('exclusion'));
      const inclusionText = inclusionField ? (cleaned[inclusionField.field_id] || '') : '';
      const exclusionText = exclusionField ? (cleaned[exclusionField.field_id] || '') : '';
      if (inclusionText.trim() || exclusionText.trim()) {
        const ieRes = await axios.post(`${API_URL}/api/studies/${studyId}/sync-inclusion-exclusion`, { inclusion: inclusionText, exclusion: exclusionText }, { withCredentials: true });
        if (ieRes.data.created) {
          toast.success(lang === 'es' ? 'Cuestionario de Inclusión-Exclusión creado' : 'Inclusion-Exclusion questionnaire created');
        }
      }
      toast.success(lb.saved);
    } catch (err) { toast.error(errText(err)); }
    setSavingData(false);
  };

  const renderProtocolField = (f) => {
    const val = protocolData[f.field_id];
    if (f.type === 'text') {
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-stone-800">{f.label}</Label>
          <Textarea data-testid={`protocol-field-${f.field_id}`} value={val || ''} onChange={e => setFieldValue(f.field_id, e.target.value)} className="bg-white border-stone-200 min-h-[60px]" disabled={!studyId} />
        </div>
      );
    }
    if (f.type === 'counter') {
      const count = parseInt(val) || 0;
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-stone-800">{f.label}</Label>
          <div className="flex items-center gap-2" data-testid={`protocol-field-${f.field_id}`}>
            <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-sm shrink-0" disabled={!studyId || count <= 0} onClick={() => setFieldValue(f.field_id, String(Math.max(0, count - 1)))}>
              <span className="text-lg font-bold">-</span>
            </Button>
            <Input type="number" min="0" value={val || ''} onChange={e => setFieldValue(f.field_id, e.target.value)} className="w-24 h-9 bg-white border-stone-200 text-center font-semibold text-lg" disabled={!studyId} />
            <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-sm shrink-0" disabled={!studyId} onClick={() => setFieldValue(f.field_id, String(count + 1))}>
              <span className="text-lg font-bold">+</span>
            </Button>
          </div>
        </div>
      );
    }
    if (f.type === 'select') {
      // URS-F240: tolerant match for "Rango especifico"/"Rango específico"/"Specific range"
      // so the From/To inputs always appear regardless of how the option is
      // stored in the DB (with/without accent, case).
      const normalize = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      const valN = normalize(val);
      const isAgeRange = f.label.toLowerCase().includes('edad')
        && (valN === 'rango especifico' || valN === 'specific range' || valN.includes('especifico'));
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-stone-800">{f.label}</Label>
          <div className="flex items-center gap-3">
            <Select value={val || ''} onValueChange={v => setFieldValue(f.field_id, v)} disabled={!studyId}>
              <SelectTrigger data-testid={`protocol-field-${f.field_id}`} className="bg-white border-stone-200"><SelectValue placeholder={lb.select_placeholder} /></SelectTrigger>
              <SelectContent>
                {(f.options || []).map(opt => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAgeRange && (
              <>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Label className="text-xs text-stone-500 whitespace-nowrap">{lang === 'es' ? 'Desde' : 'From'}</Label>
                  <Input data-testid="protocol-age-from" type="number" min="0" max="120" value={protocolData[f.field_id + '_from'] || ''} onChange={e => setFieldValue(f.field_id + '_from', e.target.value)} className="w-20 h-9 bg-white border-stone-200 text-center" disabled={!studyId} />
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Label className="text-xs text-stone-500 whitespace-nowrap">{lang === 'es' ? 'Hasta' : 'To'}</Label>
                  <Input data-testid="protocol-age-to" type="number" min="0" max="120" value={protocolData[f.field_id + '_to'] || ''} onChange={e => setFieldValue(f.field_id + '_to', e.target.value)} className="w-20 h-9 bg-white border-stone-200 text-center" disabled={!studyId} />
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    if (f.type === 'multiselect') {
      const selected = Array.isArray(val) ? val : [];
      // URS-F240: when "Rango especifico" is one of the selected age options,
      // render the From/To inputs (same UX as the select branch). Handles
      // multiselect fields where the operator may also store other options
      // simultaneously.
      const normalize = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      const isAgeRangeMS = f.label.toLowerCase().includes('edad')
        && selected.some(o => {
          const n = normalize(o);
          return n === 'rango especifico' || n === 'specific range' || n.includes('especifico');
        });
      const customKey = `_custom_options_${f.field_id}`;
      const customOptions = Array.isArray(protocolData[customKey]) ? protocolData[customKey] : [];
      const allOptions = [...(f.options || []), ...customOptions];
      const isVisits = (f.label || '').toLowerCase().includes('visit');
      const newCustomKey = `_new_custom_${f.field_id}`;
      const newCustom = protocolData[newCustomKey] || '';
      const addCustomOption = () => {
        const v = (newCustom || '').trim();
        if (!v) return;
        if (allOptions.includes(v)) {
          toast.error(lang === 'es' ? 'Ya existe esa opcion' : 'Option already exists');
          return;
        }
        setProtocolData(p => ({
          ...p,
          [customKey]: [...(Array.isArray(p[customKey]) ? p[customKey] : []), v],
          [f.field_id]: [...(Array.isArray(p[f.field_id]) ? p[f.field_id] : []), v],
          [newCustomKey]: '',
        }));
      };
      const removeCustomOption = (opt) => {
        setProtocolData(p => ({
          ...p,
          [customKey]: (Array.isArray(p[customKey]) ? p[customKey] : []).filter(o => o !== opt),
          [f.field_id]: (Array.isArray(p[f.field_id]) ? p[f.field_id] : []).filter(o => o !== opt),
        }));
      };
      return (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-stone-800">{f.label}</Label>
          <div data-testid={`protocol-field-${f.field_id}`} className="flex flex-wrap gap-2 p-3 border border-stone-200 rounded-md bg-white">
            {allOptions.length > 0 ? allOptions.map(opt => {
              const isCustom = customOptions.includes(opt);
              const isSelected = selected.includes(opt);
              return (
                <div key={opt} className="flex items-center gap-0.5">
                  <button type="button" onClick={() => studyId && toggleMultiselect(f.field_id, opt)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5
                      ${isSelected ? (isCustom ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-[#1f3e80] text-white border-[#1f3e80]')
                                   : (isCustom ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:border-emerald-400' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400')}
                      ${!studyId ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    {opt}
                    {isCustom && <span className="text-[9px] uppercase tracking-wider opacity-70">custom</span>}
                  </button>
                  {isCustom && studyId && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeCustomOption(opt); }}
                      className="text-stone-300 hover:text-red-500 transition-colors"
                      data-testid={`protocol-remove-custom-${f.field_id}-${opt}`}
                      title={lang === 'es' ? 'Eliminar opcion personalizada' : 'Remove custom option'}>
                      <Trash size={11} />
                    </button>
                  )}
                </div>
              );
            }) : <span className="text-xs text-stone-400">{lang === 'es' ? 'Sin opciones configuradas' : 'No options configured'}</span>}
          </div>
          {isAgeRangeMS && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <div className="flex items-center gap-1.5 shrink-0">
                <Label className="text-xs text-stone-500 whitespace-nowrap">{lang === 'es' ? 'Desde' : 'From'}</Label>
                <Input data-testid="protocol-age-from" type="number" min="0" max="120" value={protocolData[f.field_id + '_from'] || ''} onChange={e => setFieldValue(f.field_id + '_from', e.target.value)} className="w-20 h-9 bg-white border-stone-200 text-center" disabled={!studyId} />
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Label className="text-xs text-stone-500 whitespace-nowrap">{lang === 'es' ? 'Hasta' : 'To'}</Label>
                <Input data-testid="protocol-age-to" type="number" min="0" max="120" value={protocolData[f.field_id + '_to'] || ''} onChange={e => setFieldValue(f.field_id + '_to', e.target.value)} className="w-20 h-9 bg-white border-stone-200 text-center" disabled={!studyId} />
              </div>
            </div>
          )}
          {isVisits && studyId && (
            <div className="flex items-center gap-2 mt-2">
              <Input
                data-testid={`protocol-custom-add-input-${f.field_id}`}
                placeholder={lang === 'es' ? 'Añadir tiempo personalizado (ej. T21D)' : 'Add custom time (e.g. T21D)'}
                value={newCustom}
                onChange={e => setProtocolData(p => ({ ...p, [newCustomKey]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomOption(); } }}
                className="h-8 text-xs flex-1 max-w-xs bg-white border-stone-200 uppercase placeholder:normal-case"
              />
              <Button
                size="sm" variant="outline"
                onClick={addCustomOption}
                disabled={!newCustom.trim()}
                className="h-8 px-3 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid={`protocol-custom-add-btn-${f.field_id}`}
              >
                <Plus size={13} /> {lang === 'es' ? 'Añadir' : 'Add'}
              </Button>
              {customOptions.length > 0 && (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                  {customOptions.length} {lang === 'es' ? 'personalizadas' : 'custom'}
                </Badge>
              )}
            </div>
          )}
          {selected.length > 0 && <p className="text-xs text-stone-400">{selected.length} {lang === 'es' ? 'seleccionados' : 'selected'}</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <h2 className="text-base font-heading font-medium text-stone-800 mb-4">{lb.doc_title}</h2>
        {protocolDoc?.file_id ? (
          <div className="flex items-center gap-4 p-4 bg-stone-50 border border-stone-200 rounded-sm">
            <FilePdf size={32} className="text-red-500" weight="duotone" />
            <div className="flex-1">
              <button type="button" onClick={openDoc} className="text-sm font-medium text-[#1f3e80] hover:underline cursor-pointer" data-testid="protocol-doc-open">
                {protocolDoc.file_name}
              </button>
              {protocolDoc.uploaded_at && <p className="text-xs text-stone-400 mt-0.5">{new Date(protocolDoc.uploaded_at).toLocaleDateString()}</p>}
            </div>
            <Button variant="outline" size="sm" className="gap-1 rounded-sm" onClick={openDoc} data-testid="protocol-doc-view">
              <Eye size={14} />{lb.view}
            </Button>
            <label className="cursor-pointer">
              <Button variant="ghost" size="sm" className="gap-1 rounded-sm text-stone-500" asChild>
                <span><UploadSimple size={14} />{lb.change}</span>
              </Button>
              <input type="file" className="hidden" accept=".pdf" onChange={e => e.target.files[0] && handleUpload(e.target.files[0])} />
            </label>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 rounded-sm text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => setConfirmRemove(true)}
              disabled={removing}
              data-testid="protocol-doc-remove"
            >
              <Trash size={14} />{lb.remove}
            </Button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-stone-200 rounded-sm p-8 text-center">
            <FilePdf size={40} className="mx-auto text-stone-300 mb-3" />
            <p className="text-sm text-stone-500 mb-3">{lb.no_doc}</p>
            <label className="cursor-pointer">
              <Button variant="outline" className="gap-2 rounded-sm" disabled={uploading || !studyId} asChild>
                <span><UploadSimple size={16} />{uploading ? '...' : lb.upload}</span>
              </Button>
              <input type="file" className="hidden" accept=".pdf" onChange={e => e.target.files[0] && handleUpload(e.target.files[0])} disabled={!studyId} />
            </label>
            {!studyId && <p className="text-xs text-amber-500 mt-2">{lang === 'es' ? 'Guarda el estudio primero para poder subir el protocolo' : 'Save the study first to upload the protocol'}</p>}
          </div>
        )}
      </div>

      {(protocolFields.length > 0 || studyId) && (
        <div className="bg-white border border-stone-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-heading font-medium text-stone-800">{lb.fields_title}</h2>
            {studyId && (
              <Button data-testid="save-protocol-data-btn" onClick={saveProtocolData} disabled={savingData} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2" size="sm">
                <FloppyDisk size={14} />{savingData ? '...' : lb.save_data}
              </Button>
            )}
          </div>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Duracion del estudio' : 'Study duration'}</Label>
                <div className="flex items-center gap-2">
                  <Input data-testid="protocol-duration-value" type="number" min="1" value={protocolData._duration_value || ''} onChange={e => setFieldValue('_duration_value', e.target.value)} className="w-24 h-9 bg-white border-stone-200 text-center font-semibold" disabled={!studyId} />
                  <Select value={protocolData._duration_unit || ''} onValueChange={v => setFieldValue('_duration_unit', v)} disabled={!studyId}>
                    <SelectTrigger data-testid="protocol-duration-unit" className="w-32 bg-white border-stone-200"><SelectValue placeholder={lang === 'es' ? 'Unidad...' : 'Unit...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dias">{lang === 'es' ? 'Dias' : 'Days'}</SelectItem>
                      <SelectItem value="semanas">{lang === 'es' ? 'Semanas' : 'Weeks'}</SelectItem>
                      <SelectItem value="meses">{lang === 'es' ? 'Meses' : 'Months'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(() => {
                const pf = protocolFields.find(f => f.label.toLowerCase().includes('participantes'));
                return pf ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>{renderProtocolField(pf)}</div>
                  </div>
                ) : null;
              })()}
            </div>
            {protocolFields.filter(f => !f.label.toLowerCase().includes('participantes')).map(f => (
              <div key={f.field_id}>{renderProtocolField(f)}</div>
            ))}
            <div className="space-y-1.5 pt-2 border-t border-stone-100" data-testid="protocol-disease-groups">
              <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Criterios por Grupo de enfermedad (Qsalud)' : 'Disease group criteria (Qsalud)'}</Label>
              <DiseaseGroupFilter lang={lang} disabled={!studyId} testidPrefix="protocol-dg"
                include={protocolData._disease_groups_include || []} exclude={protocolData._disease_groups_exclude || []}
                onChange={(inc, exc) => setProtocolData(p => ({ ...p, _disease_groups_include: inc, _disease_groups_exclude: exc }))} />
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={confirmRemove}
        onOpenChange={(v) => { if (!v) setConfirmRemove(false); }}
        title={lb.confirm_remove_title}
        description={lb.confirm_remove_desc}
        onConfirm={handleRemove}
      />
    </div>
  );
}
